<script>
import api from '@/api'
import { alertSuccess } from '@/helpers/swal'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  data() {
    return {
      pagetitle: 'Rules',
      categories: [],
      selectedCategories: [],
      alertLevels: ['Critical', 'Warning', 'OK'],
      components: [
        { name: 'Device', value: 'monitoring_alert' },
        { name: 'Memory', value: 'memory_alerts' },
        { name: 'Memory EZELINK', value: 'memory_alerts_collectd' },
        { name: 'Disk', value: 'disk_alerts' },
        { name: 'Disk EZELINK', value: 'disk_alerts_collectd' },
        { name: 'CPU', value: 'cpu_alerts' },
        { name: 'CPU EZELINK', value: 'cpu_alerts_collectd' },
        { name: 'Sync Status', value: 'sync_status_alert' },
        { name: 'HA Status', value: 'ha_status_alert' },
      ],
      selectedComponents: [],
      notifyViaEmail: true,
      notifyViaSMS: false,
      notifyViaWebhook: false,
      notifyEmails: '',
      notifyPhones: '',
      notifyWebhooks: '',
      webhookFieldsExpanded: false,
      webhookMeasurementsExpanded: false,
      rule: {
        ruleName: '',
        categories: [],
        alertLevels: [],
        components: [],
        channelTypes: [],
        notifyViaEmail: [],
        notifyViaSms: [],
        notifyViaWebhook: [],
      },
      errors: {
        ruleName: '',
        categories: '',
        alertLevels: '',
        components: '',
        channelTypes: '',
        notifyViaEmail: '',
        notifyViaSms: '',
        notifyEmails: '',
        notifyWebhooks: '',
      },
      saveBtnDisabled: false,
    }
  },
  components: {
    vSelect,
  },
  async mounted() {
    await this.getCategories()
    // In case of edit rule
    const ruleId = this.$route.params.id
    if (ruleId) {
      await this.getRuleData(ruleId)
    }
  },
  methods: {
    async getCategories() {
      await api
        .get(`${window.apiUrl}/category/getCategories`)
        .then((response) => {
          this.categories = response.data || []
        })
        .finally(() => {
          this.loading = false
        })
    },

    async getRuleData(ruleId) {
      await api.get(`/alert-notification-rules/get-rule/${ruleId}`).then((response) => {
        let rule = response.data

        this.rule.id = rule.id
        this.rule.ruleName = rule.ruleName
        this.selectedComponents = this.components.filter((component) => rule.components.includes(component.value))
        this.rule.alertLevels = rule.alertLevels
        this.selectedCategories = this.categories.filter((category) => rule.categories.includes(category.id))
        this.notifyEmails = rule.emailAddresses.join('\n')
        this.notifyPhones = rule.phoneNumbers.join('\n')
        this.notifyWebhooks = (rule.webhookUrls || []).join('\n')
        this.notifyViaEmail = rule.channelTypes.includes('EMAIL')
        this.notifyViaSMS = rule.channelTypes.includes('SMS')
        this.notifyViaWebhook = rule.channelTypes.includes('WEBHOOK')
      })
    },

    saveRule() {
      if (!this.validateRequest()) return

      this.saveBtnDisabled = true
      let data = this.rule
      data.components = this.selectedComponents.map((component) => component.value)
      data.categories = this.selectedCategories.map((category) => category.id)
      data.channelTypes = []
      if (this.notifyViaEmail) {
        data.channelTypes.push('EMAIL')
      }
      if (this.notifyViaSMS) {
        data.channelTypes.push('SMS')
      }
      if (this.notifyViaWebhook) {
        data.channelTypes.push('WEBHOOK')
      }
      data.notifyViaEmail = this.notifyEmails
        .split('\n')
        .map((email) => email.trim())
        .filter((email) => email)

      data.notifyViaSms = this.notifyPhones
        .split('\n')
        .map((phone) => phone.trim())
        .filter((phone) => phone)

      data.notifyViaWebhook = this.notifyViaWebhook
        ? this.notifyWebhooks
            .split('\n')
            .map((url) => url.trim())
            .filter((url) => url)
        : []

      this.rule.id ? this.updateRule(data) : this.addRule(data)
    },

    addRule(data) {
      api
        .post('/alert-notification-rules/add-rule', data)
        .then(() => {
          alertSuccess('Rule added successfully')
          this.$router.push({ name: 'alert-rules' })
        })
        .finally(() => {
          this.saveBtnDisabled = false
        })
    },

    updateRule(data) {
      api
        .post(`/alert-notification-rules/modify-rule/${this.rule.id}`, data)
        .then(() => {
          alertSuccess('Rule updated successfully')
          this.$router.push({ name: 'alert-rules' })
        })
        .finally(() => {
          this.saveBtnDisabled = false
        })
    },

    validateRequest() {
      let isValid = true
      this.errors = {}
      const { ruleName, alertLevels } = this.rule

      if (!ruleName) {
        this.errors.ruleName = 'Rule name is required'
        isValid = false
      }
      if (!this.selectedCategories.length) {
        this.errors.categories = 'Device category is required'
        isValid = false
      }
      if (!alertLevels.length) {
        this.errors.alertLevels = 'Alert level is required'
        isValid = false
      }
      if (!this.selectedComponents.length) {
        this.errors.components = 'Component is required'
        isValid = false
      }
      if (!(this.notifyViaEmail || this.notifyViaSMS || this.notifyViaWebhook)) {
        this.errors.channelTypes = 'At least one notification channel is required'
        isValid = false
      }
      if (this.notifyViaEmail && !this.notifyEmails) {
        this.errors.notifyEmails = 'At least one email is required'
        isValid = false
      }
      if (this.notifyViaWebhook) {
        const urls = this.notifyWebhooks
          .split('\n')
          .map((url) => url.trim())
          .filter((url) => url)
        if (!urls.length) {
          this.errors.notifyWebhooks = 'At least one webhook URL is required'
          isValid = false
        } else if (urls.some((url) => !/^https?:\/\/.+/.test(url))) {
          this.errors.notifyWebhooks = 'Webhook URLs must start with http:// or https://'
          isValid = false
        }
      }
      return isValid
    },
  },
}
</script>

<template>
  <div>
    <Teleport to="#elePageTitle">
      {{ pagetitle }}
    </Teleport>
    <div class="row">
      <div class="col-xxl-6">
        <div class="card">
          <div class="card-header d-flex align-items-center">
            <h4 class="card-title">{{ $route.params.id ? 'Edit Rule' : 'Add New Rule' }}</h4>
          </div>
          <div class="card-body">
            <form @submit.prevent="saveRule()">
              <div class="row">
                <div class="col-md-12 mb-3">
                  <label for="name-input" class="form-label">Name</label>
                  <input type="text" id="name-input" class="form-control" placeholder="Enter name" v-model="rule.ruleName" />
                  <div v-if="errors.ruleName" class="invalid-feedback">{{ errors.ruleName }}</div>
                </div>
                <div class="col-md-12 mb-3">
                  <label for="category-input" class="form-label">Device Category</label>
                  <v-select v-model="selectedCategories" :options="categories" :closeOnSelect="false" multiple label="categoryName" placeholder="Select category">
                    <template #open-indicator="{ attributes, isOpen }">
                      <i class="font-size-22 fw-semibold bx" :class="isOpen ? 'bx-chevron-up' : 'bx-chevron-down'" v-bind="attributes"></i>
                    </template>
                  </v-select>
                  <div v-if="errors.categories" class="invalid-feedback">{{ errors.categories }}</div>
                </div>
                <div class="col-md-12 mb-3">
                  <label for="alertLevel-input" class="form-label">Alert Level</label>
                  <v-select v-model="rule.alertLevels" :options="alertLevels" :closeOnSelect="false" multiple placeholder="Select level">
                    <template #open-indicator="{ attributes, isOpen }">
                      <i class="font-size-22 fw-semibold bx" :class="isOpen ? 'bx-chevron-up' : 'bx-chevron-down'" v-bind="attributes"></i>
                    </template>
                  </v-select>
                  <div v-if="errors.alertLevels" class="invalid-feedback">{{ errors.alertLevels }}</div>
                </div>
                <div class="col-md-12 mb-3">
                  <label for="component-input" class="form-label">Component</label>
                  <v-select v-model="selectedComponents" :options="components" :closeOnSelect="false" multiple label="name" placeholder="Select component">
                    <template #open-indicator="{ attributes, isOpen }">
                      <i class="font-size-22 fw-semibold bx" :class="isOpen ? 'bx-chevron-up' : 'bx-chevron-down'" v-bind="attributes"></i>
                    </template>
                  </v-select>
                  <div v-if="errors.components" class="invalid-feedback">{{ errors.components }}</div>
                </div>
                <div class="invalid-feedback">{{ errors.channelTypes }}</div>
                <div class="col-md-6 mb-3 mb-md-0">
                  <div class="form-check form-switch form-switch-md mb-2" dir="ltr">
                    <input class="form-check-input" type="checkbox" id="notify-via-email" v-model="notifyViaEmail" value="1" />
                    <label class="form-check-label" for="notify-via-email">Notify via Email</label>
                  </div>
                  <div class="mt-3">
                    <label for="notifyEmails" class="form-label">
                      Emails <span class="text-muted">(one email per line)</span>
                    </label>
                    <textarea
                      id="notifyEmails"
                      class="form-control"
                      rows="5"
                      v-model="notifyEmails"
                      placeholder="Enter email addresses"
                    ></textarea>
                    <div v-if="errors.notifyEmails" class="invalid-feedback">{{ errors.notifyEmails }}</div>
                  </div>
                </div>
                <div class="col-md-6 mb-3 mb-md-0">
                  <div class="form-check form-switch form-switch-md mb-2" dir="ltr">
                    <input class="form-check-input" type="checkbox" id="notify-via-webhook" v-model="notifyViaWebhook" value="1" />
                    <label class="form-check-label" for="notify-via-webhook">Notify via Webhook</label>
                  </div>
                  <div class="mt-3">
                    <label for="notifyWebhooks" class="form-label">
                      Webhook URLs <span class="text-muted">(one URL per line)</span>
                    </label>
                    <textarea
                      id="notifyWebhooks"
                      class="form-control"
                      rows="5"
                      v-model="notifyWebhooks"
                      placeholder="https://example.com/webhook"
                    ></textarea>
                    <div v-if="errors.notifyWebhooks" class="invalid-feedback">{{ errors.notifyWebhooks }}</div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="card-footer">
            <div class="row">
              <div class="col-7">
                <router-link to="/alert/rules" class="btn btn-light d-inline-flex align-items-center">
                  <i class="bx bx-arrow-back font-size-16 me-1"></i>
                  Back to List
                </router-link>
              </div>
              <div class="col-5 d-flex justify-content-end">
                <button class="btn btn-success d-flex align-items-center" @click.prevent="saveRule()" :disabled="saveBtnDisabled">
                  <i v-if="!saveBtnDisabled" class="bx bx-check-double font-size-16"></i>
                  <i v-else class="bx bx-loader bx-spin font-size-16"></i>
                  <span class="ms-1">Save</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="notifyViaWebhook" class="col-xxl-6">
        <div class="card webhook-info-card">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="bx bx-info-circle me-1"></i>
              Webhook API Guide
            </h5>
          </div>
          <div class="card-body">
            <p class="mb-3 text-muted small">
              When an alert matches this rule, EzeNMS sends a <strong>POST</strong> with JSON to each webhook URL.
            </p>

            <h6 class="mb-2">Example webhook POST</h6>
            <pre class="webhook-example small mb-3"><code>POST https://operations.com/api/v1/alerts/webhook HTTP/1.1
Host: operations.com
Content-Type: application/json

{
  "event": "alert",
  "alertId": 1042,
  "device": "ROUTER-HOST-01",
  "time": "2026-07-16 11:30:45",
  "level": "Critical",
  "message": "CPU usage exceeded 90%",
  "status": "active",
  "category": "Production Routers",
  "measurement": "cpu_alerts",
  "field": "cpu_usage",
  "value": "94.2"
}</code></pre>

            <button
              type="button"
              class="webhook-section-toggle"
              @click="webhookFieldsExpanded = !webhookFieldsExpanded"
            >
              <i class="bx" :class="webhookFieldsExpanded ? 'bx-chevron-down' : 'bx-chevron-right'"></i>
              <span>Request fields</span>
            </button>
            <ul v-show="webhookFieldsExpanded" class="list-unstyled small mb-3 webhook-field-list">
              <li><code>event</code> — always <code>alert</code></li>
              <li><code>alertId</code> — numeric alert ID</li>
              <li><code>device</code> — monitored host name</li>
              <li><code>time</code> — <code>yyyy-MM-dd HH:mm:ss</code></li>
              <li><code>level</code> — <code>Critical</code>, <code>Warning</code>, or <code>OK</code></li>
              <li><code>message</code> — alert text</li>
              <li><code>status</code> — e.g. active / cleared</li>
              <li><code>category</code> — device category name</li>
              <li><code>measurement</code> — component type (see list below)</li>
              <li><code>field</code> / <code>value</code> — metric field and value (optional)</li>
            </ul>

            <button
              type="button"
              class="webhook-section-toggle"
              @click="webhookMeasurementsExpanded = !webhookMeasurementsExpanded"
            >
              <i class="bx" :class="webhookMeasurementsExpanded ? 'bx-chevron-down' : 'bx-chevron-right'"></i>
              <span>Possible <code>measurement</code> values</span>
            </button>
            <div v-show="webhookMeasurementsExpanded" class="webhook-measurements small mb-3">
              <code>monitoring_alert</code>
              <code>memory_alerts</code>
              <code>memory_alerts_collectd</code>
              <code>disk_alerts</code>
              <code>disk_alerts_collectd</code>
              <code>cpu_alerts</code>
              <code>cpu_alerts_collectd</code>
              <code>interface_alerts</code>
              <code>interface_error_alert</code>
              <code>web_service_alert</code>
              <code>database_service_alert</code>
              <code>monitoring_service_alert</code>
              <code>cache_service_alert</code>
              <code>zero_user_alert</code>
              <code>license_utilization_alert</code>
              <code>sync_status_alert</code>
              <code>ha_status_alert</code>
            </div>

            <h6 class="mb-2">What their API should return</h6>
            <p class="small text-muted mb-2">
              Any <strong>HTTP 2xx</strong> within ~10 seconds. Body is optional, e.g.
              <code>{ "received": true }</code>.
            </p>
            <p class="small text-muted mb-0">
              No auth headers are sent today.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.webhook-section-toggle {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  width: 100%;
  padding: 0;
  margin-bottom: 0.5rem;
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 1rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.webhook-section-toggle .bx {
  font-size: 1.15rem;
  line-height: 1;
}

.webhook-section-toggle code {
  font-weight: 600;
}

.webhook-info-card .webhook-field-list li {
  margin-bottom: 0.35rem;
}

.webhook-info-card code {
  font-size: 0.8rem;
}

.webhook-example {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 0.35rem;
  padding: 0.75rem 1rem;
  margin: 0;
  overflow-x: auto;
  white-space: pre;
}

.webhook-example code {
  background: transparent;
  color: inherit;
}

.webhook-measurements {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.webhook-measurements code {
  display: inline-block;
}
</style>
